/**
 * @module MapControls
 */
import { getLayerByName } from './utils/getLayerByName.js';

/**
 * @classdesc Manages map controls based on configuration. It uses dynamic 
 * imports to load control modules as needed, optimizing performance by avoiding
 * unnecessary initial loads.
 */

class MapControls {
  /**
   * Creates an instance of MapControls.
   */
  constructor() {
    // Keep track of the controls we've created
    this.controls = [];
  }

  /**
   * Set the language for the product manager
   * @param {string} newLanguage - The new language code (e.g., 'en', 'es')
   */
  setLanguage(newLanguage) {
    this.language = newLanguage;
  }

  /**
   * Load map controls from configuration objects.
   * This method is now called by MapManager when the map is initialized.
   * @param {object} configs 
   * @param {MapManager} mapManager
   * @returns {Promise<void>}
   */
  async loadControlsFromConfigs(configs, mapManager) {
    // Merge control configurations from all products
    const allControlConfigs = configs.flatMap(config => config.controls || []);
    const uniqueControlConfigs = this._getUniqueConfigs(allControlConfigs, 'type');

    // Separate dropdown controls from others
    const dropdownConfigs = uniqueControlConfigs.filter(c => c.container === 'dropdown');
    const rootControls = uniqueControlConfigs.filter(c => c.container !== 'dropdown');

    // Create and populate the dropdown control
    // We build these first because one control, specifically, affects the
    // contents of the dropdown (different downloads), so the dropdown elements
    // had to be built first. Don't know if that will affect future controls.
    if (dropdownConfigs.length > 0) {
      const { default: DropdownControl } = await import('./controls/DropdownControl.js');
      const dropdown = new DropdownControl();
      this.controls.push(dropdown); // Store the instance
      const dropdownContainer = dropdown.getContainerElement();
      for (const config of dropdownConfigs) {
        config.target = dropdownContainer;
        const controlInstance = await this.createControl(config, mapManager);
        if (controlInstance) {
          this.controls.push(controlInstance); // Store the instance
          dropdown.addControl(controlInstance);
        }
      }
      mapManager.map.addControl(dropdown);
    }

    // Create and add controls that live on the map root
    for (const config of rootControls) {
      config.target = mapManager.map.getTargetElement();
      const control = await this.createControl(config, mapManager);
      if (control) {
        this.controls.push(control); // Store the instance
        mapManager.map.addControl(control);
      }
    }
  }

  /**
   * Creates a control instance based on the provided configuration.
   * @param {*} config - The control configuration object.
   * @param {*} mapManager - The MapManager instance.
   * @returns {Promise<Object|null>} The created control instance or null if not created.
   */
  async createControl(config, mapManager) {
    switch (config.type) {
      case 'language':
        const { default: LanguageControl } = await import('./controls/LanguageControl.js');
        return new LanguageControl({ 
          target: config.target,
          mapManager: mapManager,
          languages: config.languages,
          currentLanguage: mapManager.language,
          label: config.label
        });
      case "opacitySlider":
        // Only create the control if there are watches and warnings
        if (config.filename) {
          const { default: OpacitySliderControl } = await import('./controls/OpacitySliderControl.js');
          return new OpacitySliderControl({ 
            target: config.target,
            map: mapManager.map,
            productId: config.productId,
            layerId: config.layerId,
            styleModule: config.styleModule
          });
        }
        else {
          return null;
        }
      case "saveGis":
        // Only create the control if config.filename !== ""
        if (config.filename) {
          const { default: SaveGisControl } = await import('./controls/SaveGisControl.js');
          return new SaveGisControl({ 
            target: config.target,
            id: config.id,
            label: config.label,
            filename: config.filename
          });
        }
        else {
          return null;
        }
      case 'savePng':
        const { default: SavePngControl } = await import('./controls/SavePngControl.js');
        return new SavePngControl({ 
          target: config.target,
          id: config.id,
          label: config.label,
          filename: config.filename
        });
      case "toggleLabels":
        const { default: ToggleLabelsControl } = await import('./controls/ToggleLabelsControl.js');
        return new ToggleLabelsControl({ 
          target: config.target,
          map: mapManager.map
        });
      case "toggleRadios":
        const { default: ToggleRadiosControl } = await import('./controls/ToggleRadiosControl.js');
        // Find the layers by their IDs
        const startLayer = getLayerByName(mapManager.map, config.startLayerId);
        const nextLayer = getLayerByName(mapManager.map, config.nextLayerId);
        const comboLayer = getLayerByName(mapManager.map, config.comboLayerId);
        return new ToggleRadiosControl({
          map: mapManager.map,
          target: document.getElementById(config.position),
          startLayer: startLayer,
          startLayerName: config.startLayerName,
          startLayerDate: config.startLayerDate,
          startLayerId: config.startLayerId,
          nextLayer: nextLayer,
          nextLayerName: config.nextLayerName,
          nextLayerDate: config.nextLayerDate,
          nextLayerId: config.nextLayerId,
          comboLayer: comboLayer,
          comboLayerName: config.comboLayerName,
          comboLayerId: config.comboLayerId,
        });
      case "zoomTo":
        const { default: ZoomToControl } = await import('./controls/ZoomToControl.js');
        return new ZoomToControl({ 
          target: config.target,
          map: mapManager.map
        });
      // ... other cases
      default:
        return null;
    }
  }

  /**
   * Utility method to get unique configurations based on a key.
   * @param {Array} configs - Array of configuration objects.
   * @param {string} key - The key to determine uniqueness.
   * @returns {Array} Array of unique configuration objects.
   */
  _getUniqueConfigs(configs, key) {
    return [...new Map(configs.map(item => [item[key], item])).values()];
  }

  /**
   * Iterates through all managed controls and tells them to update their language.
   */
  updateControlLanguages() {
    for (const control of this.controls) {
      // Check if the control has an updateLanguage method before calling it
      if (typeof control.updateLanguage === 'function') {
        control.updateLanguage();
      }
    }
  }

  addControls(controlsConfig) {
    for (const controlConfig of controlsConfig) {
      this.addControl(controlConfig);
    }
  }

  removeControl(controlId) {
    const control = this.controls[controlId];
    if (control) {
      this.map.removeControl(control);
      delete this.controls[controlId];
    }
  }

  removeControls(controlsConfig) {
    for (const controlConfig of controlsConfig) {
      this.removeControl(controlConfig.id);
    }
  }

}

export default MapControls;